import { ButtonProps, Theme, SxProps, alpha, useTheme } from "@mui/material"
import { forwardRef, PropsWithChildren, Ref } from "react"
import { LoadingButton } from "./LoadingButton"

interface Props extends ButtonProps {
    loading?: boolean
    mode?: "primary" | "secondary" | "muted" | "error"
    transparent?: boolean
    target?: string
}

const getButtonBgColor = (theme: Theme, mode: "primary" | "secondary" | "muted" | "error") => {
    if (mode === "secondary") {
        return alpha(theme.palette.secondary.light, 0.2)
    }
    if (mode === "muted") {
        return theme.palette.divider
    }
    if (mode === "error") {
        return theme.palette.error.main
    }
    return theme.palette.primary.main
}

export const RecallButton = forwardRef(function PrimaryButton(
    {
        loading,
        mode = "primary",
        transparent = false,
        sx = {},
        size = "medium",
        variant = "contained",
        children,
        ...props
    }: PropsWithChildren<Props>,
    ref: Ref<HTMLButtonElement>
) {
    const { startIcon, endIcon } = props
    const theme = useTheme()
    const isLarge = size === "large"

    const buttonStyles = {
        ...styles.buttonCommon,
        ...(mode === "primary" ? styles.buttonPrimary : {}),
        ...(mode === "secondary" ? styles.buttonSecondary : {}),
        ...(mode === "muted" ? styles.buttonMuted : {}),
        ...(transparent ? { backgroundColor: "transparent" } : {}),
        fontSize: isLarge ? 16 : undefined,
        minHeight: isLarge ? 44 : undefined,
        px: startIcon || endIcon ? 2 : 1.5,
        ...sx,
    } as SxProps<Theme>

    return (
        <LoadingButton
            ref={ref}
            bgColor={getButtonBgColor(theme, mode)}
            color={mode === "error" ? "error" : undefined}
            sx={buttonStyles}
            loading={loading}
            size={size}
            variant={variant}
            // handle mui bug where error is throw between loading states for a google translated button
            translate={loading !== undefined ? "no" : undefined}
            {...props}
        >
            {children}
        </LoadingButton>
    )
})
RecallButton.displayName = "RecallButton"

const styles: Record<string, SxProps<Theme>> = {
    buttonCommon: {
        lineHeight: 1,
        py: 1,
        px: 2,
        fontSize: 16,
        borderRadius: "8px",
        maxWidth: "400px",
    },
    buttonPrimary: {
        backgroundColor: (theme) => theme.palette.primary.main,
        color: (theme) => alpha(theme.palette.primary.contrastText, 0.95),
        "&.Mui-disabled": {
            color: (theme) => alpha(theme.palette.primary.contrastText, 0.6),
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.5),
        },
    },
    buttonSecondary: {
        color: (theme) => theme.palette.secondary.light,
        backgroundColor: (theme) => alpha(theme.palette.secondary.light, 0.2),
        "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.secondary.light, 0.3),
        },
    },
    buttonMuted: {
        color: (theme) => theme.palette.text.primary,
        backgroundColor: (theme) => theme.palette.divider,
        borderColor: (theme) => theme.palette.divider,
        "&:hover": {
            backgroundColor: (theme) => theme.palette.action.hover,
            borderColor: (theme) => theme.palette.divider,
        },
    },
}
